import React, { useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useAuth } from '../context/AuthContext'

// Stripe publishable key injected at build time via .env.production
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PK ?? '')

const CARD_STYLE = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: 'Montserrat, Segoe UI, sans-serif',
      fontSize: '15px',
      fontSmoothing: 'antialiased',
      '::placeholder': { color: 'rgba(255,255,255,0.35)' },
    },
    invalid: { color: '#ff6b6b', iconColor: '#ff6b6b' },
  },
}

// ── Step 2: Card payment form ────────────────────────────────────────────────
interface PaymentStepProps {
  clientSecret: string
  devMode: boolean
  onSuccess: (paymentIntentId: string) => Promise<void>
  onBack: () => void
}

function PaymentStep({ clientSecret, devMode, onSuccess, onBack }: PaymentStepProps) {
  const stripe   = useStripe()
  const elements = useElements()
  const [error,   setError]   = useState('')
  const [loading, setLoading] = useState(false)

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (devMode) {
        // Stripe not configured on server — skip real payment
        await onSuccess('dev_bypass')
        return
      }
      if (!stripe || !elements) throw new Error('Payment not ready')
      const card = elements.getElement(CardElement)
      if (!card) throw new Error('Card element missing')

      const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card },
      })
      if (stripeErr) throw new Error(stripeErr.message)
      if (paymentIntent?.status === 'succeeded') {
        await onSuccess(paymentIntent.id)
      } else {
        throw new Error('Payment did not complete. Please try again.')
      }
    } catch (err: any) {
      setError(err.message ?? 'Payment failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {/* Price summary */}
      <div style={{
        background: 'rgba(232,184,75,0.08)', border: '1px solid rgba(232,184,75,0.25)',
        borderRadius: 10, padding: '1rem 1.2rem', marginBottom: '1.2rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>
            🏁 Race Day Pool — 2026 Indy 500
          </div>
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', marginTop: '0.2rem' }}>
            One-time software access fee · unlimited games
          </div>
        </div>
        <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#e8b84b' }}>$3.99</div>
      </div>

      <form onSubmit={handlePay} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {devMode ? (
          <div style={{ padding: '0.9rem 1rem', background: 'rgba(255,255,255,0.06)', borderRadius: 8,
            color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', textAlign: 'center' }}>
            ⚙️ Dev mode — payment skipped (Stripe not configured)
          </div>
        ) : (
          <div style={{
            padding: '0.85rem 1rem',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
          }}>
            <CardElement options={CARD_STYLE} />
          </div>
        )}

        {error && (
          <div style={{ color: '#ff6b6b', fontSize: '0.82rem', padding: '0.5rem 0.75rem',
            background: 'rgba(255,107,107,0.1)', borderRadius: 6 }}>
            {error}
          </div>
        )}

        <button type="submit" className="btn btn-primary" disabled={loading || (!devMode && !stripe)}
          style={{ marginTop: '0.1rem' }}>
          {loading ? '…' : devMode ? 'Create Account' : '🔒 Pay $3.99 & Create Account'}
        </button>

        <div style={{ textAlign: 'center' }}>
          <button type="button" onClick={onBack}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
              fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}>
            ← Back
          </button>
        </div>

        {!devMode && (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem' }}>
            🔒 Secured by Stripe · Your card details are never stored on our servers
          </div>
        )}
      </form>
    </div>
  )
}

// ── Main AuthScreen ──────────────────────────────────────────────────────────
interface Props { onDone: () => void }

export default function AuthScreen({ onDone }: Props) {
  const { login } = useAuth()
  const [tab,          setTab]          = useState<'login' | 'register' | 'forgot'>('login')
  const [step,         setStep]         = useState<'form' | 'payment'>('form')
  const [email,        setEmail]        = useState('')
  const [name,         setName]         = useState('')
  const [password,     setPassword]     = useState('')
  const [showPass,     setShowPass]     = useState(false)
  const [error,        setError]        = useState('')
  const [success,      setSuccess]      = useState('')
  const [loading,      setLoading]      = useState(false)
  const [clientSecret, setClientSecret] = useState('')
  const [devMode,      setDevMode]      = useState(false)

  const switchTab = (t: typeof tab) => {
    setTab(t); setError(''); setSuccess(''); setStep('form')
  }

  // Step 1 submit — login, forgot, or initiate payment for register
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(''); setSuccess(''); setLoading(true)
    try {
      if (tab === 'forgot') {
        const res  = await fetch('/api/auth/forgot-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error)
        setSuccess('Check your email for a reset link.')
        return
      }

      if (tab === 'login') {
        const res  = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error)
        login(data.token, data.user)
        onDone()
        return
      }

      // Register — create payment intent, then move to payment step
      if (!name.trim()) throw new Error('Please enter your name')
      if (password.length < 8) throw new Error('Password must be at least 8 characters')

      const piRes  = await fetch('/api/auth/create-payment-intent', { method: 'POST' })
      const piData = await piRes.json()
      if (!piRes.ok) throw new Error(piData.error)

      setClientSecret(piData.clientSecret)
      setDevMode(!!piData.devMode)
      setStep('payment')
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  // Step 2 — payment succeeded, complete registration
  const handlePaymentSuccess = async (paymentIntentId: string) => {
    const res  = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, password, paymentIntentId }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)
    login(data.token, data.user)
    onDone()
  }

  return (
    <div style={{ maxWidth: 400, margin: '3rem auto', padding: '0 1rem' }}>
      {/* Logo / title */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{ fontSize: '2.5rem' }}>🏁</div>
        <h2 style={{ color: '#fff', margin: '0.5rem 0 0.25rem' }}>Race Day Pool</h2>
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', margin: 0 }}>
          {tab === 'forgot'
            ? 'Reset your password'
            : step === 'payment'
            ? 'Complete your registration'
            : 'Create and manage your race day tracker'}
        </p>
      </div>

      {/* Tab switcher — only show on form step for login/register */}
      {tab !== 'forgot' && step === 'form' && (
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: 4, marginBottom: '1.5rem' }}>
          {(['login', 'register'] as const).map(t => (
            <button key={t} onClick={() => switchTab(t)} style={{
              flex: 1, padding: '0.55rem', border: 'none', borderRadius: 8,
              background: tab === t ? '#e8b84b' : 'transparent',
              color: tab === t ? '#000' : 'rgba(255,255,255,0.55)',
              fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
              fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '0.5px',
              transition: 'all 0.15s',
            }}>
              {t === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          ))}
        </div>
      )}

      {/* ── Payment step ── */}
      {step === 'payment' && (
        <Elements stripe={stripePromise}>
          <PaymentStep
            clientSecret={clientSecret}
            devMode={devMode}
            onSuccess={handlePaymentSuccess}
            onBack={() => { setStep('form'); setError('') }}
          />
        </Elements>
      )}

      {/* ── Form step ── */}
      {step === 'form' && (
        <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {tab === 'register' && (
            <input className="form-input" placeholder="Your name"
              value={name} onChange={e => setName(e.target.value)} required />
          )}

          <input className="form-input" type="email" placeholder="Email address"
            value={email} onChange={e => setEmail(e.target.value)} required />

          {tab !== 'forgot' && (
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={showPass ? 'text' : 'password'}
                placeholder={tab === 'register' ? 'Password (min 8 characters)' : 'Password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{ paddingRight: '2.8rem' }}
              />
              <button type="button" onClick={() => setShowPass(p => !p)} style={{
                position: 'absolute', right: '0.75rem', top: '50%',
                transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(255,255,255,0.5)', fontSize: '1rem', padding: 0, lineHeight: 1,
              }} title={showPass ? 'Hide password' : 'Show password'}>
                {showPass ? '🙈' : '👁️'}
              </button>
            </div>
          )}

          {/* Price callout shown only on register tab */}
          {tab === 'register' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.65rem 0.9rem',
              background: 'rgba(232,184,75,0.08)',
              border: '1px solid rgba(232,184,75,0.2)',
              borderRadius: 8, fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)',
            }}>
              <span style={{ fontSize: '1.1rem' }}>🔒</span>
              <span>One-time software access fee of <strong style={{ color: '#e8b84b' }}>$3.99</strong> — unlimited games for the 2026 Indy 500</span>
            </div>
          )}

          {error && (
            <div style={{ color: '#ff6b6b', fontSize: '0.82rem', padding: '0.5rem 0.75rem',
              background: 'rgba(255,107,107,0.1)', borderRadius: 6 }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ color: '#4caf50', fontSize: '0.82rem', padding: '0.5rem 0.75rem',
              background: 'rgba(76,175,80,0.1)', borderRadius: 6 }}>
              {success}
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading} style={{ marginTop: '0.25rem' }}>
            {loading
              ? '…'
              : tab === 'login'
              ? 'Sign In'
              : tab === 'register'
              ? 'Next: Pay $3.99 →'
              : 'Send Reset Link'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
            {tab === 'login' && (
              <button type="button" onClick={() => switchTab('forgot')} style={{
                background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
                fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline',
              }}>
                Forgot your password?
              </button>
            )}
            {tab === 'forgot' && (
              <button type="button" onClick={() => switchTab('login')} style={{
                background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
                fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline',
              }}>
                ← Back to Sign In
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  )
}
